import axios from 'axios'

/**
 * 外部IdPログインの画面側。提供元ごとの違いは**呼び出す口の名前だけ**。
 *
 * ここがやるのは2つだけ。
 *
 *   1. サーバに「送り出す先」を聞いて、そこへブラウザを移す
 *   2. 結び付けの一覧を出し、外す
 *
 * **判断はしない。** どの組織か、戻り先はどこか、誰なのか、全部サーバが
 * 決める。画面が決められる形にすると、
 *
 *   - 戻り先を画面から渡せる → 認可コードを外へ飛ばせる
 *   - 組織を画面から渡せる  → どの組織の人でも通せる
 *
 * ということになる。行き先のURLもサーバが組み立てて返す。
 *
 * ■ なぜ提供元ごとに写さないのか
 *
 * サーバ側で、提供元を1つしか見ていない繰り返しが実際に見つかった
 * （Microsoft が使えるかを見たあと、Google の行まで Microsoft 分として
 * 数えていた）。写すと、片方だけ直して片方が残る。**入口は1本にする。**
 */

const BASE = process.env.VUE_APP_API_ORIGIN

/**
 * 提供元1つぶんの窓口を作る。
 *
 * @param {string} provider URLに出る提供元の名前（microsoft / google）
 * @param {string} availabilityKey 応答のどの鍵を見るか
 */
export function createExternalIdpLogin (provider, availabilityKey) {
  const root = BASE + '/oidc/' + provider

  /**
   * PREIKAがこの入口を開けているか、サーバに聞く。
   *
   * 画面側の設定だけで決めると、画面は開いているのにサーバは閉じている
   * （押せるのに通らない）、あるいはその逆、というずれが起きる。
   *
   * **聞けなかったら閉じている扱い。** 分からないときにボタンを出すと、
   * 押しても必ず失敗する入口を見せることになる。
   */
  async function isAvailable () {
    try {
      const response = await axios.get(root + '/availability')
      return Boolean(response.data && response.data[availabilityKey])
    } catch (error) {
      return false
    }
  }

  /**
   * 送り出す。
   *
   * サーバが state・nonce・PKCE を作ってDBへ覚え、行き先のURLを返す。
   * 画面はそこへ移るだけ。
   *
   * **メールアドレスを聞かない。** 誰かは戻ってきたトークンからサーバが
   * 決める。先に聞く形にすると、入れた時点で「その人がこの提供元を
   * 結び付けているか」が画面の反応から分かってしまう。
   */
  async function startLogin () {
    const response = await axios.post(root + '/start')
    const url = response.data && response.data.authorizationUrl
    if (!url) throw new Error(provider.toUpperCase() + '_START_FAILED')
    return url
  }

  /**
   * 本人のアカウントを結び付けるために送り出す。
   *
   * ログイン済みでないと呼べない。サーバが本人を要求の行へ入れる。
   */
  async function startLink () {
    const response = await axios.post(root + '/link/start')
    const url = response.data && response.data.authorizationUrl
    if (!url) throw new Error(provider.toUpperCase() + '_LINK_START_FAILED')
    return url
  }

  return { isAvailable, startLogin, startLink }
}

/**
 * 本人に結び付いている外部アカウントの一覧。
 *
 * **提供元では分けない。** 利用者から見れば1つの表なので、口も1つ。
 * 分けると、本人確認のやり直しや持ち主の確認を提供元のぶんだけ写す
 * ことになり、片方で1行落ちても気づけない。
 */
/**
 * いま自分が結び付けられる外部アカウント。
 *
 * 返るのは提供元ごとに2つ。
 *
 *   offered          … PREIKA がその提供元を用意しているか
 *   allowedByCompany … その会社が利用者に許しているか
 *
 * **分けて返る理由**があって、案内の向き先が変わる。用意されていない
 * のは<b>こちら（PREIKA）側</b>の話で、会社の管理者に言っても始まらない。
 * 以前は「管理者の設定が必要です」としか出せず、設定済みの管理者が
 * 何度も自社設定を見に行くことになった。
 *
 * <b>これは案内のための口で、守りではない。</b>通してよいかは、結び付けを
 * 始めるときにサーバが断る。
 */
export async function fetchLinkableProviders () {
  const response = await axios.get(BASE + '/oidc/linkable')
  return response.data || {}
}

export async function listExternalIdentities () {
  const response = await axios.get(BASE + '/oidc/identities')
  return Array.isArray(response.data) ? response.data : []
}

/**
 * 結び付けを1つ外す。
 *
 * 渡すのは行の番号だけ。誰のものかはサーバがセッションから決めるので、
 * 他人の番号を入れても届かない。外す方式も、その行の提供元から決まる。
 */
export async function unlinkExternalIdentity (identityRowId) {
  await axios.delete(BASE + '/oidc/identities/' + encodeURIComponent(identityRowId))
}

/**
 * 外すと入口が無くなる、という応答か。
 *
 * これだけは<b>利用者へそのまま見せてよい</b>。自分の状態なので、外から
 * 他人を探る材料にならない。しかも利用者が自分で直せる（先に別の入口を
 * 用意すればよい）。黙って断ると「押しても何も起きない」に見える。
 */
export function isLastSignInMethod (error) {
  const response = error && error.response
  if (!response || !response.data) return false
  return typeof response.data.message === 'string' &&
    response.data.message.indexOf('ログイン手段が無くなります') >= 0
}

/**
 * 通信そのものが届かなかったか。
 *
 * 届かなかったのか、届いて断られたのかで、利用者へ言うことが変わる。
 * 一緒にすると「やり直せば入れるのか」が分からない。
 */
export { isNetworkFailure } from './passkey'
